import { View, StyleSheet, Text, Pressable } from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import {
  Colors,
  REVENUE_CAT_ANDROID_API_KEY,
  REVENUE_CAT_IOS_API_KEY,
} from "@/constants";
import PrimaryButton from "./PrimaryButton";
import { useEffect, useState } from "react";
import Icon from "./Icon";
import { useAuth } from "@/models/useAuth";
import { App } from "@capacitor/app";
import { useUserSlice } from "@/models/useUserSlice";
import { getPlatform, isNativeApp } from "@/models/platform";
import { Purchases, PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { gotoPrivacy, gotoTerms } from "@/models/utils";
import { SubscriptionPlan } from "@/models/types";

const AlertWidgetPaywall = () => {
  const visible = useUISlice((state) => state.paywallVisible);
  const setVisible = useUISlice((state) => state.setPaywallVisible);
  const { t } = useTranslation();
  const width = useUISlice((state) => state.device.width);
  let packages = useUISlice((state) => state.revenueCat?.packages || []);
  const isNative = isNativeApp();
  const isMobile = useUISlice((state) => state.device.isMobile);

  if (!isNative) {
    const country = useUserSlice((state) => state.userData?.country);
    const priceMapper = AlertWidgetPaywall.getPriceMapper();

    packages = [
      {
        identifier: "web_annual_low_tier" as SubscriptionPlan,
        product: {
          priceString:
            priceMapper?.[country]?.["web_annual_low_tier"] || "US$99.99",
          pricePerWeekString: "US$1.92",
        },
      },
      {
        identifier: "web_weekly_low_tier" as SubscriptionPlan,
        product: {
          priceString:
            priceMapper?.[country]?.["web_weekly_low_tier"] || "US$4.99",
        },
      },
    ] as any;
  }
  const [currentPackage, setCurrentPackage] = useState<
    PurchasesPackage | undefined
  >(packages[0]);
  const yearlyDiscountApplied = currentPackage?.identifier?.includes("annual");

  useEffect(() => {
    const listenBackButton = () => {
      if (!isNative) return;

      App.addListener("backButton", () => {
        setVisible(undefined);
      });
    };

    listenBackButton();

    return () => {
      App.removeAllListeners();
    };
  }, [visible]);

  useEffect(() => {
    if (isNative) {
      if (packages) {
        setCurrentPackage(
          packages?.find((pkg) => pkg?.identifier.includes("annual"))
        );
      } else {
        AlertWidgetPaywall.configureRevenueCat();
      }
    }
  }, [packages]);

  const _toggleYearlyDiscount = () => {
    const weeklyPackage = packages?.find((pkg) =>
      pkg.identifier.includes("weekly")
    );
    const monthlyPackage = packages?.find((pkg) =>
      pkg.identifier.includes("monthly")
    );
    const yearlyPackage = packages?.find((pkg) =>
      pkg.identifier.includes("annual")
    );

    if (currentPackage?.identifier?.includes("annual")) {
      setCurrentPackage(weeklyPackage || monthlyPackage);
    } else {
      setCurrentPackage(yearlyPackage);
    }
  };

  const onPress = async () => {
    const _handlePurchase = async () => {
      if (isNative) {
        useUserSlice
          .getState()
          .subscription()
          .revenueCatPurchase(currentPackage);
      } else {
        useUserSlice
          .getState()
          .subscription()
          .stripeCheckout(currentPackage.identifier as SubscriptionPlan);
      }
    };

    if (!useAuth.isAuthenticated()) {
      useAuth.signInWithMobile({
        callback: () => {
          _handlePurchase();
        },
      });

      return;
    }

    _handlePurchase();
  };

  const _gotoTerms = () => {
    App.removeAllListeners();
    gotoTerms();
  };

  const _gotoPrivacy = () => {
    App.removeAllListeners();
    gotoPrivacy();
  };

  if (!visible) return null;

  if (isNativeApp() && !currentPackage?.product) return <View />;

  return (
    <ModalContainer
      fullScreen={isNative}
      shouldPersistBackdropClick
      title=""
      onDismiss={() => setVisible(undefined)}
      hideCancel
      hideDismissIcon
      modalWidth={Math.min(width, 600)}
    >
      <Pressable
        onPress={() => setVisible(undefined)}
        style={{
          marginTop: -ms(8),
          marginBottom: ms(16),
        }}
      >
        <Icon
          icon="dismiss"
          style={{
            width: ms(24),
            height: ms(24),
            tintColor: Colors.text + "40",
          }}
        />
      </Pressable>
      <Text
        style={{
          fontSize: ms(25),
          fontWeight: "800",
          marginBottom: ms(16),
          color: Colors.text,
          textAlign: "center",
        }}
      >
        {t("alertWidgetPaywall.title1")}
        <Text
          style={{
            backgroundColor: Colors.text,
            color: Colors.white,
            paddingHorizontal: ms(8),
            paddingVertical: ms(4),
            borderRadius: ms(8),
            marginHorizontal: ms(8),
          }}
        >
          {t("alertWidgetPaywall.title2")}
        </Text>
        {t("alertWidgetPaywall.title3")}
      </Text>

      <div
        style={{
          display: !isNative && isMobile ? "none" : "block",
          paddingTop: ms(24),
          paddingBottom: ms(24),
          alignSelf: "center",
          width: width * 0.7,
          height: ms(180),
          borderRadius: ms(60),
          overflow: "hidden",
        }}
      >
        <img
          src="/icons/paywall_banner.webp"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 20%, black 80%, transparent)",
            maskImage:
              "linear-gradient(to right, transparent, black 20%, black 80%, transparent)",
          }}
        />
      </div>

      <View
        style={{
          borderRadius: ms(12),
          backgroundColor: Colors.text + "08",
          padding: ms(16),
          gap: ms(6),
        }}
      >
        <Text
          style={{
            fontSize: ms(16),
            fontWeight: "600",
            color: Colors.text,
          }}
        >
          {t("alertWidgetPaywall.proTitle")}
        </Text>
        <Text
          style={{
            fontSize: ms(12),
            color: Colors.text,
            lineHeight: ms(16),
            fontWeight: "400",
          }}
        >
          {t("alertWidgetPaywall.proDescription")}
        </Text>
        <Text
          style={{
            fontSize: ms(12),
            color: Colors.text + "80",
            fontWeight: "600",
          }}
        >
          {currentPackage.identifier.includes("weekly") &&
            t("alertWidgetPaywall.weeklyPricing", {
              price: currentPackage?.product.priceString,
            })}

          {currentPackage.identifier.includes("monthly") &&
            t("alertWidgetPaywall.monthlyPricing", {
              price: currentPackage?.product.priceString,
            })}

          {currentPackage.identifier.includes("annual") &&
            t("alertWidgetPaywall.annualPricing", {
              pricePerWeek: currentPackage?.product.pricePerWeekString,
              annualPrice: currentPackage?.product.priceString,
            })}
        </Text>
      </View>

      <Pressable
        onPress={_toggleYearlyDiscount}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginVertical: ms(16),
          borderWidth: ms(1),
          borderColor: Colors.text + "20",
          paddingVertical: ms(12),
          paddingHorizontal: ms(20),
          borderRadius: ms(30),
        }}
      >
        <Text
          style={{
            fontSize: ms(15),
            color: Colors.text,
            fontWeight: "500",
          }}
        >
          {yearlyDiscountApplied
            ? t("alertWidgetPaywall.yearlyDiscountApplied")
            : t("alertWidgetPaywall.applyYearlyDiscount")}
        </Text>

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            padding: ms(4),
            borderRadius: ms(20),
            width: ms(56),
            height: ms(30),
            backgroundColor: Colors.text + "20",
            ...(yearlyDiscountApplied && {
              backgroundColor: Colors.text,
              justifyContent: "flex-end",
            }),
          }}
        >
          <View
            style={{
              width: ms(24),
              height: ms(24),
              borderRadius: ms(12),
              backgroundColor: Colors.white,
            }}
          />
        </View>
      </Pressable>

      <View
        style={{
          flexDirection: "row",
          gap: ms(12),
          alignItems: "center",
          marginBottom: ms(16),
        }}
      >
        <View
          style={{
            width: ms(4),
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
            marginTop: ms(14),
          }}
        >
          <View
            style={{
              width: ms(6),
              height: ms(6),
              borderRadius: ms(3),
              backgroundColor: Colors.text,
            }}
          />
          <View
            style={{
              width: ms(2),
              height: ms(20),
              backgroundColor: Colors.text + "40",
            }}
          />
          <View
            style={{
              width: ms(6),
              height: ms(6),
              borderRadius: ms(3),
              backgroundColor: Colors.text,
              marginBottom: ms(12),
            }}
          />
        </View>

        <View style={{ flex: 1, gap: ms(8) }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text style={{ fontSize: ms(13), fontWeight: "600" }}>
              {yearlyDiscountApplied
                ? t("alertWidgetPaywall.yearlyAccess")
                : t("alertWidgetPaywall.weeklyAccess")}
            </Text>
            <Text
              style={{
                backgroundColor: "transparent",
                color: "transparent",
                paddingHorizontal: ms(8),
                paddingVertical: ms(4),
                borderRadius: ms(60),
                fontSize: ms(12),
                fontWeight: "600",
                ...(yearlyDiscountApplied && {
                  backgroundColor: Colors.success,
                  color: Colors.white,
                }),
              }}
            >
              {t("alertWidgetPaywall.getEightMonthFree")}
            </Text>
          </View>

          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text
              style={{
                color: Colors.text + "80",
                fontWeight: "400",
                fontSize: ms(13),
              }}
            >
              {t("alertWidgetPaywall.dueTodayLabel")}
            </Text>
            <Text
              style={{
                fontWeight: "300",
                color: Colors.text + "80",
                fontSize: ms(13),
              }}
            >
              {currentPackage?.product.priceString}
            </Text>
          </View>
        </View>
      </View>

      <PrimaryButton
        style={{
          backgroundColor: Colors.text,
          height: ms(64),
          justifyContent: "center",
          alignItems: "center",
          borderRadius: ms(120),
        }}
        text={t("alertWidgetPaywall.buttonContinue")}
        onPress={onPress}
      />

      <View style={styles.footer}>
        <Text onPress={_gotoTerms} style={styles.footerLink}>
          {t("alertWidgetPaywall.terms")}
        </Text>
        <Text onPress={_gotoPrivacy} style={styles.footerLink}>
          {t("alertWidgetPaywall.privacy")}
        </Text>
        {isNative && (
          <Text
            onPress={
              useUserSlice.getState().subscription().revenueCatRestorePurchase
            }
            style={styles.footerLink}
          >
            {t("alertWidgetPaywall.restore")}
          </Text>
        )}
      </View>
      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

AlertWidgetPaywall.configureRevenueCat = async () => {
  if (isNativeApp()) {
    const user = useAuth.currentAuthUser();

    Purchases.configure({
      appUserID: user?.userId,
      apiKey:
        getPlatform() === "ios"
          ? REVENUE_CAT_IOS_API_KEY
          : REVENUE_CAT_ANDROID_API_KEY,
    }).then(async () => {
      const customerInfo = await Purchases.getCustomerInfo().then(
        (res) => res.customerInfo
      );

      const offerings = await Purchases.getOfferings();

      useUISlice.getState().setRevenueCat({
        packages: offerings?.current?.availablePackages?.slice(0, 2) || [],
        customerInfo,
      });
    });
  }
};

AlertWidgetPaywall.getPriceMapper = () => {
  return {
    US: {
      web_weekly_low_tier: "US$4.99",
      web_annual_low_tier: "US$99.99",
    },
    CA: {
      web_weekly_low_tier: "US$4.99 ≈ CA$7.19",
      web_annual_low_tier: "US$99.99 ≈ CA$143.99",
    },
    GB: {
      web_weekly_low_tier: "US$4.99 ≈ £4.09",
      web_annual_low_tier: "US$99.99 ≈ £81.99",
    },
    AU: {
      web_weekly_low_tier: "US$4.99 ≈ AU$7.98",
      web_annual_low_tier: "US$99.99 ≈ AU$159.98",
    },
    DE: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    FR: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    IT: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    ES: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    NL: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    BE: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    AT: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    CH: {
      web_weekly_low_tier: "US$4.99 ≈ CHF4.54",
      web_annual_low_tier: "US$99.99 ≈ CHF90.99",
    },
    SE: {
      web_weekly_low_tier: "US$4.99 ≈ SEK54.89",
      web_annual_low_tier: "US$99.99 ≈ SEK1099.89",
    },
    NO: {
      web_weekly_low_tier: "US$4.99 ≈ NOK56.89",
      web_annual_low_tier: "US$99.99 ≈ NOK1139.89",
    },
    DK: {
      web_weekly_low_tier: "US$4.99 ≈ DKK35.89",
      web_annual_low_tier: "US$99.99 ≈ DKK719.29",
    },
    FI: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    IE: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    PT: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    GR: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    JP: {
      web_weekly_low_tier: "US$4.99 ≈ ¥777",
      web_annual_low_tier: "US$99.99 ≈ ¥15,568",
    },
    KR: {
      web_weekly_low_tier: "US$4.99 ≈ ₩7,458",
      web_annual_low_tier: "US$99.99 ≈ ₩149,338",
    },
    IN: {
      web_weekly_low_tier: "US$4.99 ≈ ₹432",
      web_annual_low_tier: "US$99.99 ≈ ₹8,659",
    },
    BR: {
      web_weekly_low_tier: "US$4.99 ≈ R$30.20",
      web_annual_low_tier: "US$99.99 ≈ R$604.94",
    },
    MX: {
      web_weekly_low_tier: "US$4.99 ≈ MX$103.29",
      web_annual_low_tier: "US$99.99 ≈ MX$2,069.79",
    },
    SG: {
      web_weekly_low_tier: "US$4.99 ≈ S$7.03",
      web_annual_low_tier: "US$99.99 ≈ S$140.84",
    },
    HK: {
      web_weekly_low_tier: "US$4.99 ≈ HK$40.23",
      web_annual_low_tier: "US$99.99 ≈ HK$806.36",
    },
    NZ: {
      web_weekly_low_tier: "US$4.99 ≈ NZ$9.18",
      web_annual_low_tier: "US$99.99 ≈ NZ$184.05",
    },
    ZA: {
      web_weekly_low_tier: "US$4.99 ≈ R96.16",
      web_annual_low_tier: "US$99.99 ≈ R1,926.64",
    },
    TH: {
      web_weekly_low_tier: "US$4.99 ≈ ฿176.00",
      web_annual_low_tier: "US$99.99 ≈ ฿3,527.64",
    },
    MY: {
      web_weekly_low_tier: "US$4.99 ≈ RM23.14",
      web_annual_low_tier: "US$99.99 ≈ RM463.47",
    },
    ID: {
      web_weekly_low_tier: "US$4.99 ≈ Rp84,445",
      web_annual_low_tier: "US$99.99 ≈ Rp1,692,071",
    },
    PH: {
      web_weekly_low_tier: "US$4.99 ≈ ₱302.50",
      web_annual_low_tier: "US$99.99 ≈ ₱6,062.01",
    },
    VN: {
      web_weekly_low_tier: "US$4.99 ≈ ₫125,751",
      web_annual_low_tier: "US$99.99 ≈ ₫2,519,750",
    },
    TR: {
      web_weekly_low_tier: "US$4.99 ≈ ₺184.23",
      web_annual_low_tier: "US$99.99 ≈ ₺3,690.59",
    },
    PL: {
      web_weekly_low_tier: "US$4.99 ≈ zł21.23",
      web_annual_low_tier: "US$99.99 ≈ zł425.11",
    },
    CZ: {
      web_weekly_low_tier: "US$4.99 ≈ Kč125.64",
      web_annual_low_tier: "US$99.99 ≈ Kč2,517.32",
    },
    HU: {
      web_weekly_low_tier: "US$4.99 ≈ Ft2,053",
      web_annual_low_tier: "US$99.99 ≈ Ft41,127",
    },
    RO: {
      web_weekly_low_tier: "US$4.99 ≈ lei24.83",
      web_annual_low_tier: "US$99.99 ≈ lei497.58",
    },
    BG: {
      web_weekly_low_tier: "US$4.99 ≈ лв9.76",
      web_annual_low_tier: "US$99.99 ≈ лв195.58",
    },
    HR: {
      web_weekly_low_tier: "US$4.99 ≈ €4.84",
      web_annual_low_tier: "US$99.99 ≈ €96.99",
    },
    IL: {
      web_weekly_low_tier: "US$4.99 ≈ ₪18.47",
      web_annual_low_tier: "US$99.99 ≈ ₪370.16",
    },
    AE: {
      web_weekly_low_tier: "US$4.99 ≈ AED18.33",
      web_annual_low_tier: "US$99.99 ≈ AED367.36",
    },
    SA: {
      web_weekly_low_tier: "US$4.99 ≈ SAR18.71",
      web_annual_low_tier: "US$99.99 ≈ SAR374.96",
    },
  };
};

const styles = StyleSheet.create({
  negativeMargin: {
    marginBottom: -ms(12),
  },
  features: {
    marginBottom: ms(16),
  },
  featureContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: ms(8),
    marginBottom: ms(6),
  },
  discountText: {
    fontSize: ms(12),
    fontWeight: "500",
    lineHeight: ms(24),
    color: Colors.text,
    paddingHorizontal: ms(12),
    paddingVertical: ms(1),
    backgroundColor: "#00ff00" + "20",
    borderRadius: ms(8),
    marginLeft: ms(12),
  },
  avatar: {
    borderWidth: ms(2),
    borderColor: Colors.text + "30",
    borderRadius: ms(24),
    width: ms(18),
    height: ms(18),
    marginLeft: -ms(2),
  },
  featureText: {
    fontSize: ms(14),
    fontWeight: "400",
    lineHeight: ms(24),
    color: Colors.text,
  },
  tickIcon: {
    tintColor: "#15803d",
    width: ms(16),
    height: ms(16),
  },
  planButton: {
    borderWidth: ms(2),
    height: ms(60),
    paddingHorizontal: ms(16),
    borderRadius: ms(12),
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: ms(12),
  },
  radioOuter: {
    width: ms(18),
    height: ms(18),
    borderRadius: ms(16),
    backgroundColor: Colors.text + "30",
    alignItems: "center",
    justifyContent: "center",
  },
  radioInner: {
    width: ms(14),
    height: ms(14),
    backgroundColor: Colors.white,
    borderRadius: ms(8),
  },
  radioOuterSelected: {
    width: ms(18),
    height: ms(18),
    borderRadius: ms(16),
    backgroundColor: Colors.accent,
    alignItems: "center",
    justifyContent: "center",
  },
  radioInnerSelected: {
    width: ms(8),
    height: ms(8),
    backgroundColor: Colors.white,
    borderRadius: ms(8),
  },
  planContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: ms(12),
  },
  planTitle: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
  },
  planPrice: {
    fontSize: ms(14),
    color: Colors.text,
  },
  plansContainer: {
    marginVertical: ms(16),
  },
  joinContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: ms(12),
    gap: ms(4),
  },
  avatarsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: -ms(4),
  },
  joinText: {
    marginLeft: ms(8),
    fontSize: ms(12),
    color: Colors.subtitle,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "center",
    gap: ms(32),
    marginTop: ms(12),
  },
  footerLink: {
    fontSize: ms(14),
    color: Colors.subtitle,
  },
});

export default AlertWidgetPaywall;
